import React, {useEffect} from 'react';
import {makeStyles} from 'tss-react/mui';
import {useParams} from 'react-router-dom';
import {useIntl} from 'react-intl';
import CircularProgress from '@mui/material/CircularProgress';
import {RenewalClickProvider, useRenewalClick} from './context/RenewalClickContext';
import {SCREENS, screenToStepIndex} from './constants/renewalClick';
import {fetchRenewalPageModel} from './api/renewalClickApi';
import MinimalHeader from '../DocumentCollection/components/MinimalHeader';
import Stepper from '../../components/common/Stepper';

import OfferScreen from './screens/OfferScreen';
import PaymentScreen from './screens/PaymentScreen';
import SigningPendingScreen from './screens/SigningPendingScreen';
import CompletionScreen from './screens/CompletionScreen';
import AuthScreen from './screens/AuthScreen';
import InvalidLinkScreen from './screens/InvalidLinkScreen';
import ErrorScreen from './screens/ErrorScreen';

const useStyles = makeStyles()((theme) => ({
  root: {
    minHeight: '100vh',
    backgroundColor: theme.palette.background.paper,
    direction: 'ltr',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    flexDirection: 'column',
    gap: 16,
  },
  loadingText: {
    fontSize: theme.typography.pxToRem(16),
    fontFamily: 'Ploni, Arial, sans-serif',
    color: theme.palette.text.secondary,
  },
}));

const SCREENS_WITH_STEPPER = new Set([
  SCREENS.OFFER, SCREENS.PAYMENT,
  SCREENS.SIGNING_PENDING, SCREENS.COMPLETION,
]);



const RENEWAL_STEPS = [
  {label: 'הצעה'},
  {label: 'תשלום'},
  {label: 'חתימה'},
  {label: 'אישור'},
];

const RenewalClickInner = () => {
  const {classes} = useStyles();
  const {shortLinkKey} = useParams();
  const intl = useIntl();
  const {currentScreen, contact, setPageData, setError} = useRenewalClick();

  useEffect(() => {
    const init = async () => {
      try {
        const data = await fetchRenewalPageModel(shortLinkKey);
        setPageData(shortLinkKey, data);
      } catch (err) {
        setError(err.message || 'Failed to load page');
      }
    };
    init();
  }, [shortLinkKey, setPageData, setError]);

  const stepIndex = screenToStepIndex(currentScreen);
  const showStepper = SCREENS_WITH_STEPPER.has(currentScreen);


  const renderScreen = () => {
    switch (currentScreen) {
    case SCREENS.LOADING:
      return (
        <div className={classes.loadingContainer}>
          <CircularProgress size={40} />
          <span className={classes.loadingText}>
            {intl.formatMessage({id: 'renewalClick.loading'})}
          </span>
        </div>
      );
    case SCREENS.OFFER:
      return <OfferScreen />;
    case SCREENS.AUTH:
      return <AuthScreen />;
    case SCREENS.PAYMENT:
      return <PaymentScreen />;
    case SCREENS.SIGNING_PENDING:
      return <SigningPendingScreen />;
    case SCREENS.COMPLETION:
      return <CompletionScreen />;
    case SCREENS.EXPIRED:
      return <InvalidLinkScreen variant='expired' />;
    case SCREENS.INVALID:
      return <InvalidLinkScreen variant='invalid' />;
    case SCREENS.ERROR:
      return <ErrorScreen />;
    default:
      return <ErrorScreen />;
    }
  };

  return (
    <div className={classes.root} dir='ltr'>
      <MinimalHeader whatsappNumber={contact.whatsAppNumber} phoneNumber={contact.phoneNumber} />
      {showStepper && <Stepper steps={RENEWAL_STEPS} activeStep={stepIndex} />}
      {renderScreen()}

    </div>
  );
};

const RenewalClickPage = () => (
  <RenewalClickProvider>
    <RenewalClickInner />
  </RenewalClickProvider>
);

export default RenewalClickPage;
